import {
  DEDUP_KEY_LENGTH,
  JSON_STRINGIFY_INDENT,
  MAX_DISPLAY_VALUE_LENGTH,
  MAX_STRING_LENGTH,
  MIN_SYSTEM_LENGTH,
  REASONING_PREFIX_PATTERNS,
  STRUCTURE_DEPTH_MAX,
  STRUCTURE_INDENT_BASE,
  STRUCTURE_INDENT_MULTIPLIER,
  STRUCTURE_TAG_COLORS,
  SYSTEM_MARKERS,
} from "./ai-conversation.constants";
import { IStructureNode } from "./ai-conversation.models";

export const isSystemText = (text: string): boolean => {
  if (!text || text.length < 100) {
    return false;
  }
  if (text.length > MIN_SYSTEM_LENGTH && text.includes("INTERNAL ONLY")) {
    return true;
  }
  return SYSTEM_MARKERS.some(m => text.trimStart().startsWith(m));
};

export const keepOnlyThinkingPart = (text: string): string => {
  const idx = text.indexOf("<answer>");
  if (idx === -1) {
    return text;
  }
  return text.slice(0, idx).trim();
};

export const safeJsonParse = <T>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const stripReasoningPrefix = (raw: string): string => {
  if (!raw?.trim()) {
    return raw ?? "";
  }
  let s = raw.trim();
  for (const re of REASONING_PREFIX_PATTERNS) {
    const m = s.match(re);
    if (m) {
      s = s.slice(m[0].length).trim();
      break;
    }
  }
  return s;
};

const deepStripStrings = (value: unknown): unknown => {
  if (typeof value === "string") {
    return stripReasoningPrefix(value);
  }
  if (Array.isArray(value)) {
    return value.map(deepStripStrings);
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = deepStripStrings(v);
    }
    return out;
  }
  return value;
};

export const parseRawReasoningToJsonArray = (raw: string): unknown[] => {
  if (!raw?.trim()) {
    return [];
  }
  const parts = raw.split(/\n\n+/);
  const result: unknown[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    try {
      result.push(deepStripStrings(JSON.parse(trimmed) as unknown) as unknown);
    } catch {
      result.push({ _raw: stripReasoningPrefix(trimmed) });
    }
  }
  return result;
};

export const extractTextsFromModelInputText = (inputText: string): string[] => {
  const obj = safeJsonParse<{
    system?: string;
    messages?: { content?: string }[];
  }>(inputText);
  if (!obj) {
    return [];
  }
  const out: string[] = [];
  const messages = obj.messages;
  if (Array.isArray(messages)) {
    for (const msg of messages) {
      const c = msg?.content;
      if (typeof c === "string" && c.trim() && !isSystemText(c)) {
        out.push(c.trim());
      }
    }
  }
  return out;
};

export const extractTextFromModelOutputContent = (
  contentStr: string,
): string[] => {
  const obj = safeJsonParse<{
    output?: { message?: { content?: { text?: string | null }[] } };
  }>(contentStr);
  if (!obj?.output?.message?.content) {
    return [];
  }
  const out: string[] = [];
  for (const item of obj.output.message.content) {
    const t = item?.text;
    if (typeof t === "string" && t.trim() && !isSystemText(t)) {
      out.push(t.trim());
    }
  }
  return out;
};

export const collectTextsFromTrace = (traceObj: unknown): string[] => {
  const collected: string[] = [];
  const trace =
    traceObj && typeof traceObj === "object"
      ? (traceObj as Record<string, unknown>)
      : null;
  if (!trace?.trace || typeof trace.trace !== "object") {
    return collected;
  }

  const orch = (trace.trace as Record<string, unknown>).orchestrationTrace;
  if (!orch || typeof orch !== "object") {
    return collected;
  }

  const o = orch as Record<string, unknown>;

  if (typeof o.rationale === "object" && o.rationale !== null) {
    const text = (o.rationale as Record<string, unknown>).text;
    if (typeof text === "string" && text.trim() && !isSystemText(text)) {
      collected.push(text.trim());
    }
  }

  if (
    typeof o.modelInvocationInput === "object" &&
    o.modelInvocationInput !== null
  ) {
    const text = (o.modelInvocationInput as Record<string, unknown>).text;
    if (typeof text === "string" && text.trim()) {
      collected.push(...extractTextsFromModelInputText(text));
    }
  }

  if (
    typeof o.modelInvocationOutput === "object" &&
    o.modelInvocationOutput !== null
  ) {
    const raw = (o.modelInvocationOutput as Record<string, unknown>)
      .rawResponse as Record<string, unknown> | undefined;
    const content = raw?.content;
    if (typeof content === "string" && content.trim()) {
      collected.push(...extractTextFromModelOutputContent(content));
    }
  }

  if (typeof o.invocationInput === "object" && o.invocationInput !== null) {
    const kb = (o.invocationInput as Record<string, unknown>)
      .knowledgeBaseLookupInput as Record<string, unknown> | undefined;
    const text = kb?.text;
    if (typeof text === "string" && text.trim()) {
      collected.push(`[Knowledge base query] ${text.trim()}`);
    }
  }

  return collected;
};

export const formatReasoningToReadable = (reasoning: string): string => {
  if (!reasoning.trim()) {
    return "";
  }
  const parts = reasoning.split(/\n\n+/);
  const allTexts: string[] = [];
  const seen = new Set<string>();

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) {
      continue;
    }
    const parsed = safeJsonParse<unknown>(trimmed);
    if (parsed !== null && typeof parsed === "object") {
      const texts = collectTextsFromTrace(parsed);
      for (const t of texts) {
        const thinkingOnly = keepOnlyThinkingPart(t)
          .replace(/\s*<\/?thinking>\s*/gi, "\n")
          .replace(/\n{3,}/g, "\n\n")
          .trim();
        if (!thinkingOnly) {
          continue;
        }
        const key = thinkingOnly.slice(0, DEDUP_KEY_LENGTH);
        if (!seen.has(key)) {
          seen.add(key);
          allTexts.push(thinkingOnly);
        }
      }
    }
  }

  if (allTexts.length === 0) {
    return "";
  }
  return allTexts.join("\n\n");
};

const collectStructureNodes = (
  value: unknown,
  depth: number,
  keyName: string,
  out: IStructureNode[],
): void => {
  if (value === null) {
    out.push({ depth, key: keyName, type: "null", value });
    return;
  }
  if (Array.isArray(value)) {
    out.push({ depth, key: keyName, type: "[]", value });
    if (value.length > 0) {
      collectStructureNodes(value[0], depth + 1, "0", out);
      if (value.length > 1) {
        out.push({
          depth: depth + 1,
          key: `… +${value.length - 1}`,
          type: undefined,
        });
      }
    }
    return;
  }
  if (typeof value === "object") {
    out.push({ depth, key: keyName, type: undefined, value });
    const obj = value as Record<string, unknown>;
    for (const k of Object.keys(obj).sort()) {
      collectStructureNodes(obj[k], depth + 1, k, out);
    }
    return;
  }
  const t = typeof value;
  out.push({ depth, key: keyName, type: t === "string" ? "string" : t, value });
};

export const buildStructureTree = (
  fullInfoSrc: unknown[],
): IStructureNode[] => {
  const out: IStructureNode[] = [];
  if (fullInfoSrc.length === 0) {
    return out;
  }
  const single =
    fullInfoSrc.length === 1 &&
    fullInfoSrc[0] !== null &&
    typeof fullInfoSrc[0] === "object" &&
    !Array.isArray(fullInfoSrc[0]);
  if (single) {
    const obj = fullInfoSrc[0] as Record<string, unknown>;
    for (const k of Object.keys(obj).sort()) {
      collectStructureNodes(obj[k], 0, k, out);
    }
  } else {
    fullInfoSrc.forEach((item, idx) => {
      collectStructureNodes(item, 0, `[${idx}]`, out);
    });
  }
  return out;
};

export const getColorKeyForClass = (key: string): string => {
  const char = key.charAt(0);
  if (/[a-z]/i.test(char)) {
    return char.toLowerCase();
  }
  if (/\d/.test(char)) {
    return "DIGIT_0";
  }
  return "_";
};

export const getEmojiForType = (type?: string): string => {
  if (!type) {
    return "📦";
  }
  if (type === "[]") {
    return "📚";
  }
  if (type === "string") {
    return "📝";
  }
  if (type === "number") {
    return "🔢";
  }
  if (type === "boolean") {
    return "✅";
  }
  if (type === "null") {
    return "⭕";
  }
  return "🔹";
};

export const formatValueForTooltip = (value: unknown): string => {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}...`
      : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value) || typeof value === "object") {
    try {
      const jsonStr = JSON.stringify(value, null, JSON_STRINGIFY_INDENT);
      return jsonStr.length > MAX_STRING_LENGTH
        ? `${jsonStr.slice(0, MAX_STRING_LENGTH)}...`
        : jsonStr;
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export const formatValueForDisplay = (value: unknown): string => {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undefined";
  }
  if (typeof value === "string") {
    return value.length > MAX_DISPLAY_VALUE_LENGTH
      ? `${value.slice(0, MAX_DISPLAY_VALUE_LENGTH)}...`
      : value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return String(value);
};

export const getTypeClass = (type?: string): string => {
  if (!type) {
    return "";
  }
  if (type === "[]") {
    return "type-array";
  }
  if (type === "string") {
    return "type-string";
  }
  if (type === "number") {
    return "type-number";
  }
  if (type === "boolean") {
    return "type-boolean";
  }
  if (type === "null") {
    return "type-null";
  }
  return "";
};

export const triggerDownloadTextFile = (
  content: string,
  filename: string,
): void => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const getStructureDepthStyles = (): string =>
  Array.from(
    { length: STRUCTURE_DEPTH_MAX + 1 },
    (_, d) =>
      `.structure-depth-${d} { padding-inline-start: ${d * STRUCTURE_INDENT_MULTIPLIER + STRUCTURE_INDENT_BASE}px; }`,
  ).join("\n");

export const getStructureKeyColorStyles = (): string =>
  Object.entries(STRUCTURE_TAG_COLORS)
    .map(([k, v]) => `.structure-key-${k} { color: ${v}; }`)
    .join("\n");
